/**
 * Licensed Materials - Property of IBM
 * IBM Watson Analytics (C) Copyright IBM Corp. 2018, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React, { Component } from 'react';
import { SVGIcon } from 'ca-ui-toolkit';
import SlotEditorItem from './SlotEditorItem';
import DndSlotItemHandler from './DndSlotItemHandler';
import StringResources from '../nls/StringResources';
import classNames from 'classnames';

import PropTypes from 'prop-types';

const GlassUtils = require('gemini/lib/@waca/baglass/js/baglass/utils/Utils');
import $ from 'jquery';
import './slotEditor.scss';

const _ = require('underscore');  //TODO: should try to remove this dependency
const AGGREGATION_TYPE_MAP = {
	none: 'none',
	countDistinct: 'countDistinct'
};

/**
 * The SlotEditorSlot component is responsible for rendering a single slot and its items.
 * It also manages drag and drop of items or data to the 'slot heading region'.
 */
class SlotEditorSlot extends Component {

	constructor(props) {
		super(props);
		this.slotEditorSlotRef = React.createRef();
	}

	componentDidMount() {
		this._initializeDndSupport();
		this._applySlotCoachMarks();
	}

	componentDidUpdate() {
		this._applySlotCoachMarks();
	}

	/**
	 * Apply coach marks on the slot or data item
	 * The slot coach mark consists with:
	 * - showPopover: boolean flag indicating whether the popover show along with the coach mark.
	 * - mappedOnly: boolean flag indicating whether the coach mark applies when the slot is mapped or
	 * 		regardless of the mapped state.
	 * - titleResource: resource name for the coach title.
	 * - contentResource: resource name for the coach content.
	 */
	_applySlotCoachMarks = _.debounce(() => {
		const { slotAPI: slot, slotDataItemHandler: { visualization, dashboardApi } } = this.props;
		const slotDef = slot.getDefinition();
		const coachMark = slotDef.getCoachMark();
		const slotId = slotDef.getId();
		const $slotEditorSlot = $(this.slotEditorSlotRef.current);
		if (coachMark) {
			const isMapped = slot.getDataItemList().length > 0;
			if (!coachMark.mappedOnly || isMapped) {
				const $el = isMapped ? $slotEditorSlot.find('.slotEditorItem') : $slotEditorSlot;
				if($el.length) {
					// Make the coachmark unique for each slot of every visualization
					const options = {
						id: visualization.getDefinition().getId() + '#' + slotId,
						$el,
						title: StringResources.get(coachMark.titleResource),
						contents: StringResources.get(coachMark.contentResource),
						showPopover: coachMark.showPopover,
						placement: 'bottom'
					};
					dashboardApi.prepareGlassOptions(options);
					GlassUtils.addCoachmark(options);
				}
			}
		} else {
			const $coachMarkNode = $slotEditorSlot.find('.coachMark');
			if($coachMarkNode.length) {
				const coachMark = $coachMarkNode[0];
				const coachMarkParent = coachMark.parentElement;
				if(coachMarkParent) {
					coachMarkParent.classList.remove('coachMarkContainer');
					coachMarkParent.removeAttribute('id');
					coachMarkParent.removeChild(coachMark);
				}
			}
		}
	});

	/**
	 * Called to render the data slots (or a single layer worth of slots for the multi-layer scenario).
	 * @param slotAPIs - the slots to render
	 * @param pLayerId - (optional) the layerId to render
	 * @param className - (optional) the custom class name to render
	 */
	render() {
		/* a custom class name will be added iff exists */
		const classes = classNames({
			slotEditor__slots__slot: true,
			[`${this.props.className}`]: this.props.className
		});
		const handler =  new DndSlotItemHandler(this.domNode, 'dndHover', this._handleDropSlotItemOnTop.bind(this), this._handleAcceptDropSlotItem.bind(this));
		const slotDefinition = this.props.slotAPI.getDefinition();
		const slotCaption = slotDefinition.getCaption();
		const slotId = slotDefinition.getId();
		return <div ref={this.slotEditorSlotRef} className={classes}>
			<div className="slotEditor__slots__slot__heading"
				ref={(node) => { this.domNode = node; }}
				tabIndex="0"
				aria-label={slotCaption}
				title={slotCaption}
				role="Listbox"
				data-slot-id={slotId}
				aria-required={this._isRequiredField(this.props.slotAPI)}
				onKeyUp= { (event) => this.props.a11Y.slotHandleKeyUp(event, handler, this.props.slotDataItemHandler) }
				onKeyPress={ (event) => this.props.a11Y.handleKeyPress(event, this.props.slotDataItemHandler.swapSlotItems.bind(this.props.slotDataItemHandler), /*dropAfter*/true) }>
				<div className="slotEditor__slots__slot__heading__container">
					<div className="slotEditor__slots__slot__heading__icon">
						<SVGIcon intent={'primary'} iconId={this._getSlotIcon()} size="small"/>
					</div>
					{slotCaption}
					{(this._isRequiredField(this.props.slotAPI)) ? <div className="slotEditor__slots__slot__heading__required">*</div> : []}
				</div>
				<div className="slotEditor__slots__slot__heading__dropBefore">
					<div className="slotEditor__slots__slot__heading__dropBefore__indicator"
					/>
				</div>
			</div>
			{this._renderSlotDataItems()}
		</div>;
	}

	_isRequiredField(slotAPI) {
		return (!slotAPI.getDefinition().isOptional()) && slotAPI.getDefinition().getShowRequiredMarker();
	}

	/**
	 * Render all data items assigned to this slot.
	 */
	_renderSlotDataItems() {
		if (this.props.dataItemAPIs.length) {
			const slotHasUnavailableColumns = this.props.slotAPI.hasUnavailableMetadataColumns();
			return _.map(this.props.dataItemAPIs, (dataItemAPI, indexInSlot) => {
				const slotId=this.props.slotAPI.getDefinition().getId();
				return <SlotEditorItem key={slotId + dataItemAPI.getId()}
					itemName={this._getSlotItemLabel(indexInSlot, slotHasUnavailableColumns)}
					columnId={dataItemAPI.getColumnId()}
					dashboardApi={this.props.slotEditorModel.dashboardApi}
					slotDataItemHandler={this.props.slotEditorModel.getSlotDataItemHandler(this.props.slotAPI, indexInSlot)}
					dropSlotItemHandler = {new DndSlotItemHandler(this.domNode, 'dndHover', this._handleDropSlotItemOnTop.bind(this), this._handleAcceptDropSlotItem.bind(this))}
					indexInSlot={indexInSlot}
					a11Y={this.props.a11Y}
					dndManager={this.props.dndManager}/>;
			});
		}
	}

	_getSlotItemLabel(itemIndex, slotHasUnavailableColumns) {
		const dataItem = this.props.slotAPI.getDataItemList()[itemIndex];
		let label = dataItem.getLabel();
		if (slotHasUnavailableColumns) {
			// check if the current data item is the missing one
			const metadataColumn = dataItem.getMetadataColumn();
			if (!metadataColumn) {
				label = StringResources.get('missingColumn', { 'columnLabel': label });
			}
		}
		return label;
	}
	/**
	 * Slot icons are retrieved from the dashboard slots and definition.
	 * If there is no icon assigned to the slot, the default for category or value is returned.
	 * @param slotAPI - the slotAPI to return the icon for.
	 */
	_getSlotIcon() {
		const iconId = this.props.slotAPI.getDefinition().getIcon();
		const iconsFeature = this.props.slotEditorModel.dashboardApi.getFeature('Icons');
		const icon = iconsFeature.getIcon(iconId);
		if(icon){
			return icon.id;
		}
		return (this._getFinalSlotType(this.props.slotAPI)=== 'ordinal') ? iconsFeature.getIcon('values').id : iconsFeature.getIcon('category').id;
	}

	/**
	* Return the 'final slot type' ==> meaning that if the slot type is 'any' and data is mapped to the slot,
	* the type of the data will be used to infer the type of the slot.
	* (Not to be confused with getEffectiveType...see below)
	* @param slotAPI - the slotAPI of interest
	* @param [dataItemIndex=0] - the data item in question
	* @returns the final slot type for this slotAPI.
	* TODO: this api returns legacy slot types.  We need to change to categorical and continuous throughout the code.
	* This function is copied from the old SlotAPI.getFinalSlotType
	* Story 295271: Add missing API to get layer caption and to replace the old API slotAPI.itemsNotSupported
	*/
	_getFinalSlotType(slotAPI, dataItemIndex = 0) {
		const layerId = slotAPI.getDefinition().getDatasetIdList()[0];
		if (layerId === 'latitude' || layerId === 'longitude') {
			return 'category';
		}
		const slotType = slotAPI.getDefinition().getType();
		if (slotType === 'any') {
			if (this._isMultiMeasuresSlot(slotAPI)) {
				return 'category';
			}
			var mappedDataItem = slotAPI.getDataItemList()[dataItemIndex];
			if (mappedDataItem) {
				if( mappedDataItem.getBinning()) {
					return 'category';
				}
				if (mappedDataItem.getType() === 'attribute') {
					const dataItemAggregationType = mappedDataItem.getAggregation ? mappedDataItem.getAggregation() : mappedDataItem.getAggregationType && mappedDataItem.getAggregationType();
					if (dataItemAggregationType === AGGREGATION_TYPE_MAP.none) {
						return 'category';
					} else {
						return 'ordinal';
					}
				} else if (mappedDataItem.getType() === 'fact') {
					/**
					 * - If one of the items in an 'any' slot is a measure, and we have more than one items,
					 * then simply consider slot type to be category.
					 * - Also look at the 'repeats' attribute by looking at the slot definition. If 'repeats'
					 * attribute is there, then it means that we don't stack the data items in this slot. This
					 * occurs in all non-VIPR visualizations.
					 * - For al non-VIPR visualizations we don't want to enforce
					 * the slot type to be a 'category', like we do for all VIPR visualizations.
					 * This prevents issues described in defect #255087
					 */
					// TODO: repeats (old API)
					if (slotAPI.getDataItemList().length > 1 && !slotAPI.getDefinition().repeats) {
						return 'category';
					} else {
						return 'ordinal';
					}
				}
			}
		}
		return slotType;
	}

	_isMultiMeasuresSlot(slot) {
		return slot.getDataItemList().find(dataItem => dataItem.getId() === '_multiMeasuresSeries');
	}

	//DRAG AND DROP SUPPORT....
	_initializeDndSupport() {
		//A data slot can be a drop target but not a dragdrop source.
		this.props.dndManager.then(
			(dndManager) => {
				if (this.domNode) {
					this._dndManager = dndManager;
					this.dropSlotItemHandler = new DndSlotItemHandler(this.domNode, 'dndHover', this._handleDropSlotItemOnTop.bind(this), this._handleAcceptDropSlotItem.bind(this), dndManager);
					dndManager.addDropTarget(this.domNode, this.dropSlotItemHandler);
				}
			}
		);
	}

	_handleDropSlotItemOnTop(dragObject) {
		if (dragObject.type ==='SLOT_ITEM') {
			this.props.slotDataItemHandler.swapSlotItems(this.dropSlotItemHandler.getData(dragObject), /*addAfter*/true);
		} else {
			this.props.slotDataItemHandler.insertAfterItem(this.dropSlotItemHandler.getData(dragObject));
		}
	}

	//Can we accept an item at the head of the item list (after -1)
	_handleAcceptDropSlotItem(dragObject) {
		// Cannot accept widget to SlotEditorSlot scope; must be within SlotEditorItem
		return this.props.slotDataItemHandler.accepts(this.dropSlotItemHandler.getData(dragObject), /*after*/true);
	}
}

SlotEditorSlot.propTypes = {
	className: PropTypes.string,
	slotAPI: PropTypes.object,
	dataItemAPIs: PropTypes.array,
	slotEditorModel: PropTypes.object.isRequired,
	dndManager: PropTypes.object.isRequired,
	slotDataItemHandler: PropTypes.object,
	a11Y: PropTypes.object
};

export default SlotEditorSlot;



// WEBPACK FOOTER //
// ./src/js/OldSlotEditorComponents/SlotEditorSlot.js